import React, { useRef, useEffect, useState } from "react";
import "./QueryOutputSection.scss";
import QueryOutputNavbar from "./QueryOutputNavbar/QueryOutputNavbar";
import { LuMinimize2, LuMaximize2 } from "react-icons/lu";
import { FaSortDown, FaSortUp } from "react-icons/fa6";
import { updateTableData } from "../../../../../../../api/database_gui.js";
import { toast } from "react-toastify";

function QueryOutputSection({
  results = [],
  error = null,
  message,
  resourceId,
  systemUserId,
  columnInfo = [],
  onHeightChange,
  resourceAccessID,
  selectedDatabase,
  currentQuery = "",
  onRefreshResults,
}) {
  const [height, setHeight] = useState(60);
  const [activeTab, setActiveTab] = useState("");
  const [queryExecuted, setQueryExecuted] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [zIndex, setZIndex] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [columnWidths, setColumnWidths] = useState({});
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, columnKey, originalValue }
  const [editValue, setEditValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [sortedResults, setSortedResults] = useState([]);
  
  const containerRef = useRef(null);
  const resizeRef = useRef(null);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const isResizing = useRef(false);
  const currentResizeColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (results.length > 0) {
      setActiveTab("Data Output");
      setQueryExecuted(true);
      setCurrentPage(1);
      // Initialize column widths if not set
      if (Object.keys(columnWidths).length === 0) {
        const initialWidths = {};
        Object.keys(results[0]).forEach((key, index) => {
          initialWidths[index] = 150; // Default width
        });
        setColumnWidths(initialWidths);
      }
      // Apply sorting if configured
      applySorting(results, sortConfig);
    } else if (error) {
      setActiveTab("Message");
      setQueryExecuted(true);
    } else {
      setActiveTab("Message");
    }
  }, [results, error, columnWidths, sortConfig]);

  // Apply sorting to results
  const applySorting = (data, config) => {
    if (!config.key) {
      setSortedResults(data);
      return;
    }

    const sorted = [...data].sort((a, b) => {
      const aValue = a[config.key];
      const bValue = b[config.key];

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return config.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Convert to string for comparison
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (aString < bString) {
        return config.direction === 'asc' ? -1 : 1;
      }
      if (aString > bString) {
        return config.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setSortedResults(sorted);
  };

  // Handle column header click for sorting
  const handleColumnSort = (columnKey) => {
    let direction = 'asc';
    
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        // Remove sorting if clicking the same column for the third time
        setSortConfig({ key: null, direction: 'asc' });
        setSortedResults(results);
        return;
      }
    }
    
    const newSortConfig = { key: columnKey, direction };
    setSortConfig(newSortConfig);
    applySorting(results, newSortConfig);
  };

  // Get sort indicator for column header
  const getSortIndicator = (columnKey) => {
    let Icon;
    if (sortConfig.key !== columnKey) {
      // Show neutral sort icon for unsorted columns
      Icon = FaSortUp; // Using FaSortUp as neutral indicator
    } else {
      // Show directional icon for sorted column
      Icon = sortConfig.direction === 'asc' ? FaSortUp : FaSortDown;
    }
    
    return (
      <span className={`database-gui-sort-indicator ${sortConfig.key === columnKey ? 'active' : 'neutral'}`}>
        <Icon />
      </span>
    );
  };

  // Focus the input when editing starts
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const totalPages = Math.ceil((sortedResults.length > 0 ? sortedResults : results).length / itemsPerPage);
  const currentItems = (sortedResults.length > 0 ? sortedResults : results).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const newHeight = startHeight.current + (startY.current - e.clientY);
        const newHeightPercentage = (newHeight / window.innerHeight) * 100;
        setHeight(Math.min(Math.max(newHeightPercentage, 20), 80));
        onHeightChange(Math.min(Math.max(newHeightPercentage, 20), 80));
        document.body.style.userSelect = "none";
      }
    };

    const handleMouseUp = () => {
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    const handleMouseDown = (e) => {
      startY.current = e.clientY;
      startHeight.current = containerRef.current.getBoundingClientRect().height;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    if (resizeRef.current) {
      resizeRef.current.addEventListener("mousedown", handleMouseDown);
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getMessageContent = () => {
    if (error) {
      return <div className="error-message">{error}</div>;
    }
    if (message) {
      return <div className="info-message">{message}</div>;
    }
    if (!queryExecuted) {
      return (
        <div className="database-gui-message-tab">
          There is no message to display
        </div>
      );
    }
    return null;
  };

  const downloadCSV = () => {
    const data = results;
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((header) => {
          const value = row[header];
          // Handle null/undefined values for CSV export
          if (value === null || value === undefined) {
            return JSON.stringify("NULL");
          }
          return JSON.stringify(value || "");
        }).join(",")
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "data-output.csv");
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMaximize = () => {
    setIsMaximized((prev) => {
      const newMaximized = !prev;
      setHeight(newMaximized ? 100 : 60);
      setZIndex(newMaximized ? 10 : 1);
      return newMaximized;
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Column resizing handlers
  const handleColumnResizeStart = (e, columnIndex) => {
    e.preventDefault();
    isResizing.current = true;
    currentResizeColumn.current = columnIndex;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnIndex] || 150;
    
    document.addEventListener('mousemove', handleColumnResizeMove);
    document.addEventListener('mouseup', handleColumnResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleColumnResizeMove = (e) => {
    if (!isResizing.current) return;
    
    const deltaX = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + deltaX); // Minimum width of 50px
    
    setColumnWidths(prev => ({
      ...prev,
      [currentResizeColumn.current]: newWidth
    }));
  };

  const handleColumnResizeEnd = () => {
    isResizing.current = false;
    currentResizeColumn.current = null;
    document.removeEventListener('mousemove', handleColumnResizeMove);
    document.removeEventListener('mouseup', handleColumnResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Inline editing functions
  const handleCellDoubleClick = (rowIndex, columnKey, value) => {
    // Don't allow editing the row number column
    if (columnKey === undefined) return;
    
    // Don't allow editing if there are no results
    if (results.length === 0) return;
    
    // Don't allow editing if already editing another cell
    if (editingCell) return;
    
    setEditingCell({ rowIndex, columnKey, originalValue: value });
    setEditValue(value === null || value === undefined ? "" : String(value));
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleEditBlur = () => {
    // Small delay to allow for Enter key handling
    setTimeout(() => {
      if (editingCell) {
        handleSaveEdit();
      }
    }, 100);
  };

  const handleSaveEdit = async () => {
    if (!editingCell || isUpdating) return;

    const { rowIndex, columnKey, originalValue } = editingCell;
    const newValue = editValue.trim();
    
    // If value hasn't changed, just cancel
    const originalValueString = originalValue === null || originalValue === undefined ? "" : String(originalValue);
    if (newValue === originalValueString) {
      handleCancelEdit();
      return;
    }

    // Basic validation - allow empty strings to be converted to NULL
    // This allows users to clear a field by making it empty

    setIsUpdating(true);
    
    try {
      // Get the actual row data from the current page
      const actualRowIndex = (currentPage - 1) * itemsPerPage + rowIndex;
      const rowData = results[actualRowIndex];
      
      // Validate the row data for updates
      const validation = validateRowForUpdate(rowData, columnKey);
      
      if (!validation.valid) {
        toast.error(`${validation.error} ${validation.suggestion}`);
        handleCancelEdit();
        return;
      }
      
      const { identifier, nonNullColumns } = validation;
      
      // Create the update data with proper row identification
      const updateData = {
        row_data: rowData,
        column_name: columnKey,
        new_value: newValue === "" ? null : newValue,
        primary_key_column: identifier.column,
        primary_key_value: identifier.value,
        identifier_type: identifier.type,
        row_index: actualRowIndex // Keep for backward compatibility
      };

      // Extract table name from the current query
      const tableName = extractTableName(currentQuery);
      if (!tableName) {
        toast.error("Could not determine table name from query. Please use a simple SELECT query.");
        handleCancelEdit();
        return;
      }
      
      const response = await updateTableData(
        resourceId,
        systemUserId,
        selectedDatabase,
        tableName,
        updateData,
        resourceAccessID
      );

      if (response.error) {
        
        // Provide more specific error messages based on common issues
        let errorMessage = response.error;
        if (response.error.includes('Cannot identify row for update')) {
          errorMessage = `Row identification failed. The backend couldn't find a unique row to update. This might happen if:
          1. The row data doesn't contain enough unique identifiers
          2. The row has been modified by another user
          3. The table structure doesn't have proper primary keys
          
          Try refreshing the data and ensure your query includes primary key columns.`;
        } else if (response.error.includes('Row not found')) {
          errorMessage = `The row you're trying to update no longer exists. This might happen if:
          1. The row was deleted by another user
          2. The data has been modified since you loaded it
          
          Please refresh the data and try again.`;
        }
        
        toast.error(`Update failed: ${errorMessage}`);
        handleCancelEdit();
      } else {
        toast.success("Data updated successfully");
        handleCancelEdit();
        // Trigger a refresh of the query results
        if (onRefreshResults) {
          onRefreshResults();
        }
      }
    } catch (error) {
      toast.error(`Update failed: ${error.message}`);
      handleCancelEdit();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  // Check if a cell is currently being edited
  const isEditing = (rowIndex, columnKey) => {
    return editingCell && 
           editingCell.rowIndex === rowIndex && 
           editingCell.columnKey === columnKey;
  };

  // Function to extract table name from query
  const extractTableName = (query) => {
    if (!query) return null;
    
    // Use the original query (not uppercase) to preserve case
    const originalQuery = query;
    
    // Handle SELECT queries
    if (originalQuery.toUpperCase().includes('SELECT')) {
      const fromMatch = originalQuery.match(/FROM\s+([^\s,;()]+)/i);
      if (fromMatch) {
        let tableName = fromMatch[1].replace(/[`"\[\]]/g, ''); // Remove quotes and brackets
        // Handle fully qualified table names (database.table)
        if (tableName.includes('.')) {
          tableName = tableName.split('.').pop(); // Get the last part after the dot
        }
        return tableName;
      }
    }
    
    // Handle UPDATE queries
    if (originalQuery.toUpperCase().includes('UPDATE')) {
      const updateMatch = originalQuery.match(/UPDATE\s+([^\s,;()]+)/i);
      if (updateMatch) {
        let tableName = updateMatch[1].replace(/[`"\[\]]/g, '');
        // Handle fully qualified table names (database.table)
        if (tableName.includes('.')) {
          tableName = tableName.split('.').pop(); // Get the last part after the dot
        }
        return tableName;
      }
    }
    
    // Handle INSERT queries
    if (originalQuery.toUpperCase().includes('INSERT')) {
      const insertMatch = originalQuery.match(/INSERT\s+INTO\s+([^\s,;()]+)/i);
      if (insertMatch) {
        let tableName = insertMatch[1].replace(/[`"\[\]]/g, '');
        // Handle fully qualified table names (database.table)
        if (tableName.includes('.')) {
          tableName = tableName.split('.').pop(); // Get the last part after the dot
        }
        return tableName;
      }
    }
    
    // Handle DELETE queries
    if (originalQuery.toUpperCase().includes('DELETE')) {
      const deleteMatch = originalQuery.match(/FROM\s+([^\s,;()]+)/i);
      if (deleteMatch) {
        let tableName = deleteMatch[1].replace(/[`"\[\]]/g, '');
        // Handle fully qualified table names (database.table)
        if (tableName.includes('.')) {
          tableName = tableName.split('.').pop(); // Get the last part after the dot
        }
        return tableName;
      }
    }
    
    return null;
  };

  // Function to get column type information with identifier badges
  const getColumnTypeWithIdentifier = (columnName) => {
    const baseType = getColumnType(columnName);
    const identifierType = getColumnIdentifierType(columnName);
    
    if (!identifierType) {
      return `(${baseType})`;
    }
    
    const identifierMap = {
      primary_key: 'PK',
      unique_identifier: 'UID', 
      auto_increment: 'AI'
    };
    
    const identifierText = identifierMap[identifierType];
    if (!identifierText) {
      return `(${baseType})`;
    }
    
    return `(${baseType}) [${identifierText}]`;
  };

  // Function to get column type information
  const getColumnType = (columnName) => {
    // columnInfo contains column names from the query result
    // We need to infer types from the actual data
    if (columnInfo && columnInfo.length > 0) {
      if (results.length > 0) {
        const sampleValue = results[0][columnName];
        if (sampleValue !== null && sampleValue !== undefined) {
          if (typeof sampleValue === 'number') {
            return Number.isInteger(sampleValue) ? 'INT' : 'DECIMAL';
          } else if (typeof sampleValue === 'string') {
            // Try to determine if it's a date/time
            if (sampleValue.match(/^\d{4}-\d{2}-\d{2}/) || 
                sampleValue.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
              return 'DATETIME';
            }
            return 'VARCHAR';
          } else if (sampleValue instanceof Date) {
            return 'DATETIME';
          } else if (typeof sampleValue === 'boolean') {
            return 'BOOLEAN';
          }
        }
      }
    }
    
    // Fallback: infer from data if columnInfo is not available
    if (results.length > 0) {
      const sampleValue = results[0][columnName];
      if (sampleValue !== null && sampleValue !== undefined) {
        if (typeof sampleValue === 'number') {
          return Number.isInteger(sampleValue) ? 'INT' : 'DECIMAL';
        } else if (typeof sampleValue === 'string') {
          // Try to determine if it's a date/time
          if (sampleValue.match(/^\d{4}-\d{2}-\d{2}/) || 
              sampleValue.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
            return 'DATETIME';
          }
          return 'VARCHAR';
        } else if (sampleValue instanceof Date) {
          return 'DATETIME';
        } else if (typeof sampleValue === 'boolean') {
          return 'BOOLEAN';
        }
      }
    }
    return 'TEXT'; // Default fallback
  };

  // Function to get column identifier type for visual indicators
  const getColumnIdentifierType = (columnName) => {
    if (results.length === 0) return null;
    
    const sampleRow = results[0];
    if (!sampleRow || !sampleRow[columnName]) return null;
    
    // Check if this column is likely a primary key or unique identifier
    const primaryKeyNames = ['id', 'ID', 'Id', '_id', 'pk', 'primary_key', 'rowid', 'ROWID'];
    const uniqueIdentifierNames = ['uuid', 'UUID', 'guid', 'GUID', 'key', 'KEY', 'code', 'CODE'];
    
    const lowerColumnName = columnName.toLowerCase();
    
    if (primaryKeyNames.includes(lowerColumnName)) {
      return 'primary_key';
    } else if (uniqueIdentifierNames.includes(lowerColumnName)) {
      return 'unique_identifier';
    } else if (typeof sampleRow[columnName] === 'number' && sampleRow[columnName] > 0) {
      // Check if it looks like an auto-increment ID
      if (primaryKeyNames.some(pk => lowerColumnName.includes(pk))) {
        return 'auto_increment';
      }
    }
    
    return null;
  };

  // Helper function to find the best identifier for a row
  const findRowIdentifier = (rowData, excludeColumn = null) => {
    // Priority 1: Common primary key names
    const primaryKeyNames = ['id', 'ID', 'Id', '_id', 'pk', 'primary_key', 'rowid', 'ROWID'];
    for (const pkName of primaryKeyNames) {
      if (rowData.hasOwnProperty(pkName) && 
          rowData[pkName] !== null && 
          rowData[pkName] !== undefined && 
          pkName !== excludeColumn) {
        return { column: pkName, value: rowData[pkName], type: 'primary_key' };
      }
    }
    
    // Priority 2: Unique identifier names
    const uniqueIdentifierNames = ['uuid', 'UUID', 'guid', 'GUID', 'key', 'KEY', 'code', 'CODE'];
    for (const uidName of uniqueIdentifierNames) {
      if (rowData.hasOwnProperty(uidName) && 
          rowData[uidName] !== null && 
          rowData[uidName] !== undefined && 
          uidName !== excludeColumn) {
        return { column: uidName, value: rowData[uidName], type: 'unique_identifier' };
      }
    }
    
    // Priority 3: Auto-increment like columns (numeric, non-zero)
    const autoIncrementNames = ['id', 'ID', 'Id', '_id', 'pk', 'primary_key'];
    for (const aiName of autoIncrementNames) {
      if (rowData.hasOwnProperty(aiName) && 
          typeof rowData[aiName] === 'number' && 
          rowData[aiName] > 0 && 
          aiName !== excludeColumn) {
        return { column: aiName, value: rowData[aiName], type: 'auto_increment' };
      }
    }
    
    // Priority 4: First non-null, non-excluded column
    const firstNonNullColumn = Object.keys(rowData).find(key => 
      rowData[key] !== null && 
      rowData[key] !== undefined && 
      key !== excludeColumn
    );
    
    if (firstNonNullColumn) {
      return { 
        column: firstNonNullColumn, 
        value: rowData[firstNonNullColumn], 
        type: 'fallback' 
      };
    }
    
    return null;
  };

  // Helper function to validate row data for updates
  const validateRowForUpdate = (rowData, columnKey) => {
    const identifier = findRowIdentifier(rowData, columnKey);
    
    if (!identifier) {
      return {
        valid: false,
        error: "No suitable identifier found for this row. Please ensure your query includes a primary key or unique identifier column.",
        suggestion: "Try including columns like 'id', 'uuid', or any unique identifier in your SELECT query."
      };
    }
    
    // Check if the row has enough unique data
    const nonNullColumns = Object.keys(rowData).filter(key => 
      rowData[key] !== null && 
      rowData[key] !== undefined && 
      key !== columnKey
    );
    
    if (nonNullColumns.length < 1) {
      return {
        valid: false,
        error: "Insufficient data to identify this row uniquely. The row needs at least one non-null column besides the one being updated.",
        suggestion: "Try including more columns in your SELECT query to provide better row identification."
      };
    }
    
    return {
      valid: true,
      identifier,
      nonNullColumns
    };
  };

  return (
    <div
      className={`database-gui-query-output-section-container ${
        isMaximized ? "maximized" : ""
      }`}
    >
      <div
        className="database-gui-query-output-section"
        style={{ height: `${height}vh`, zIndex: zIndex }}
        ref={containerRef}
      >
        <div className="database-gui-query-output-section-content">
          <div className="database-gui-query-output-section-Navbar">
            <QueryOutputNavbar
              onTabChange={handleTabChange}
              activeTab={activeTab}
              onExport={downloadCSV}
              onMaximize={handleMaximize}
              // Pagination props
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={results.length}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showPagination={activeTab === "Data Output" && results.length > 0}
            />
          </div>
          <div className="database-gui-query-output-section-results">
            {activeTab === "Data Output" && results.length > 0 && (
              <>
                <table className="database-gui-query-output-section-table">
                  <thead className="database-gui-table-header-query-output-section">
                    <tr>
                      <th className="database-gui-row-number-column"> </th>
                      {Object.keys(results[0]).map((key, index) => (
                        <th 
                          key={key} 
                          style={{ width: columnWidths[index] || 150 }}
                          className="database-gui-resizable-column database-gui-sortable-column"
                          onClick={() => handleColumnSort(key)}
                        >
                          <div className="database-gui-column-header-content">
                            <span className="database-gui-column-name">
                              {key}
                              {getSortIndicator(key)}
                            </span>
                            <span className="database-gui-column-type">{getColumnTypeWithIdentifier(key)}</span>
                          </div>
                          <div 
                            className="database-gui-column-resizer"
                            onMouseDown={(e) => handleColumnResizeStart(e, index)}
                          ></div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={index}>
                        <td className="database-gui-row-number-column">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        {Object.values(row).map((value, idx) => (
                          <td 
                            key={idx}
                            style={{ width: columnWidths[idx] || 150 }}
                          >
                            {isEditing(index, Object.keys(row)[idx]) ? (
                              <div className="database-gui-edit-cell-container">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={handleEditChange}
                                  onKeyDown={handleEditKeyDown}
                                  onBlur={handleEditBlur}
                                  ref={editInputRef}
                                  className={`database-gui-edit-input ${isUpdating ? 'database-gui-editing-loading' : ''}`}
                                  disabled={isUpdating}
                                />
                                {isUpdating && (
                                  <div className="database-gui-edit-loading-indicator">
                                    Saving...
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span
                                className="database-gui-editable-cell"
                                onDoubleClick={() => handleCellDoubleClick(index, Object.keys(row)[idx], value)}
                                title="Double-click to edit"
                              >
                                {value === null || value === undefined ? (
                                  <span className="database-gui-null-value">
                                    NULL
                                  </span>
                                ) : (
                                  value
                                )}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {activeTab === "Data Output" && results.length === 0 && !error && (
              <div className="database-gui-data-message">
                No data output. Execute a query to get output
              </div>
            )}
            {activeTab === "Message" && (
              <div className="database-gui-message-content">{getMessageContent()}</div>
            )}
          </div>
        </div>
        <div className="database-gui-query-output-section-resize" ref={resizeRef}></div>
      </div>
    </div>
  );
}

export default QueryOutputSection;
